/* eslint-disable camelcase */
import { isNil } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import Checkbox from "PFComponents/checkbox/checkbox";
import InputFieldSet from "PFComponents/text/input_field_set";
import AttachmentIcon from "PFIcons/attachment.svg";
import { Id } from "PFTypes/id";
import { SamlConfig } from "PFTypes/saml_config";
import React, { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import {
  createSamlConfig,
  NewSamlConfig,
  parseSamlConfigMetadata,
  updateSamlConfig
} from "../services/saml_configs_api";
import css from "./edit_saml_config_page.module.scss";
import SamlConfigField from "./parts/saml_config_field";

type InputData = {
  name: keyof SamlConfig;
  value: string;
  handleChange: Dispatch<SetStateAction<string>>;
  [key: string]: any;
};

type EditSamlConfigPageProps = {
  id?: Id;
  samlConfig: SamlConfig;
};

const EditSamlConfigPage = ({ samlConfig, id }: EditSamlConfigPageProps): JSX.Element => {
  const history = useHistory();
  const growl = useGrowl();
  const { t: defaultT } = useTranslation();
  const { t } = useTranslation("samlConfigs", { keyPrefix: "edit" });

  const [active, setActive] = useState(samlConfig.active);
  const [authContext, setAuthContext] = useState(samlConfig.authn_context);
  const [issuer, setIssuer] = useState(samlConfig.issuer);
  const [idpEntityId, setIdpEntityId] = useState(samlConfig.idp_entity_id);
  const [idpCert, setIdpCert] = useState(samlConfig.idp_cert);
  const [idpCertFingerprint, setIdpCertFingerprint] = useState(samlConfig.idp_cert_fingerprint);
  const [idpSsoServiceUrl, setIdpSsoServiceUrl] = useState(samlConfig.idp_sso_service_url);
  const [idpSloServiceUrl, setIdpSloServiceUrl] = useState(samlConfig.idp_slo_service_url);
  const [nameIdentifierFormat, setNameIdentifierFormat] = useState(samlConfig.name_identifier_format);
  const [serviceProviderMetadata, setServiceProviderMetadata] = useState(
    samlConfig.service_provider_metadata
  );
  const [metadataUrl, setMetadataUrl] = useState(samlConfig.metadata_url);
  const [validateRecipient, setValidateRecipient] = useState(samlConfig.validate_recipient);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const isNew = isNil(id);

  const publishSamlConfig = () => {
    const payload: SamlConfig | NewSamlConfig = {
      id,
      active,
      issuer,
      authn_context: authContext,
      idp_cert: idpCert,
      idp_cert_fingerprint: idpCertFingerprint,
      idp_entity_id: idpEntityId,
      idp_slo_service_url: idpSloServiceUrl,
      idp_sso_service_url: idpSsoServiceUrl,
      metadata_url: metadataUrl,
      name_identifier_format: nameIdentifierFormat,
      service_provider_metadata: serviceProviderMetadata,
      validate_recipient: validateRecipient
    };

    (isNew ? createSamlConfig(payload as NewSamlConfig) : updateSamlConfig(id, payload as SamlConfig))
      .then(() => {
        history.push("/admin/saml_configs");
      })
      .catch((errors) => {
        setErrors(errors.responseJSON.errors);
      });
  };

  const parseMetadata = useCallback((file: ChangeEvent<HTMLInputElement>) => {
    if (file.target.files?.[0].type === "text/xml") {
      const fileToUpload = file.target.files[0];
      const formData = new FormData();
      formData.append("metadata", new Blob([fileToUpload]), fileToUpload.name);
      formData.append("type", "text/xml");

      parseSamlConfigMetadata(formData)
        .then((resp) => {
          resp.active && setActive(resp.active);
          resp.authn_context && setAuthContext(resp.authn_context);
          resp.idp_cert && setIdpCert(resp.idp_cert);
          resp.idp_cert_fingerprint && setIdpCertFingerprint(resp.idp_cert_fingerprint);
          resp.idp_entity_id && setIdpEntityId(resp.idp_entity_id);
          resp.idp_slo_service_url && setIdpSloServiceUrl(resp.idp_slo_service_url);
          resp.idp_sso_service_url && setIdpSsoServiceUrl(resp.idp_sso_service_url);
          resp.issuer && setIssuer(resp.issuer);
          resp.metadata_url && setMetadataUrl(resp.metadata_url);
          resp.name_identifier_format && setNameIdentifierFormat(resp.name_identifier_format);
          resp.service_provider_metadata && setServiceProviderMetadata(resp.service_provider_metadata);
          resp.validate_recipient && setValidateRecipient(resp.validate_recipient);
        })
        .catch((resp: any) => {
          growl({
            message: resp?.responseJSON?.errors?.content?.[0] ?? t("unknownErrorOccured"),
            kind: "error"
          });
        });
    } else {
      growl({
        title: t("invalidFileType"),
        message: t("onlyXMLSupported"),
        kind: "error"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputsData: InputData[] = [
    { name: "authn_context", value: authContext, handleChange: setAuthContext },
    { name: "issuer", value: issuer, handleChange: setIssuer },
    { name: "idp_entity_id", value: idpEntityId, handleChange: setIdpEntityId },
    {
      name: "idp_cert",
      value: idpCert,
      handleChange: setIdpCert,
      inputType: "textarea",
      minHeight: 100,
      style: { width: "100%", resize: "vertical" }
    },
    { name: "idp_cert_fingerprint", value: idpCertFingerprint, handleChange: setIdpCertFingerprint },
    { name: "idp_sso_service_url", value: idpSsoServiceUrl, handleChange: setIdpSsoServiceUrl },
    { name: "idp_slo_service_url", value: idpSloServiceUrl, handleChange: setIdpSloServiceUrl },
    {
      name: "name_identifier_format",
      value: nameIdentifierFormat,
      handleChange: setNameIdentifierFormat
    },
    {
      name: "service_provider_metadata",
      value: serviceProviderMetadata,
      handleChange: setServiceProviderMetadata
    },
    { name: "metadata_url", value: metadataUrl, handleChange: setMetadataUrl, disabled: !isNew }
  ];

  return (
    <div className={css.wrapper}>
      <h1 className={css.header}>{isNew ? t("createSamlConfig") : t("editSamlConfig")}</h1>
      <PageBox style={{ paddingTop: 24 }}>
        <SamlConfigField label={t("metadata")}>
          <label htmlFor="metadata">
            <Button kind="tertiary" tag="a">
              <AttachmentIcon style={{ height: 20, width: 20, marginRight: 10 }} />
              {t("chooseFiles")}
            </Button>
          </label>
          <input
            id="metadata"
            type="file"
            style={{ display: "none" }}
            multiple={false}
            onChange={parseMetadata}
          />
        </SamlConfigField>
        {inputsData.map(({ name, ...props }) => (
          <SamlConfigField label={name} key={name}>
            <InputFieldSet {...props} error={errors[name]} controlledValue />
          </SamlConfigField>
        ))}
        <SamlConfigField label="validate_recipient">
          <Checkbox
            label={defaultT("yes")}
            style={{ marginBottom: 0 }}
            checked={validateRecipient}
            handleChange={setValidateRecipient}
          />
        </SamlConfigField>
        <div className={css.footer}>
          <ButtonLink kind="tertiary" href="/admin/saml_configs">
            {defaultT("cancel")}
          </ButtonLink>
          <Button text={isNew ? defaultT("create") : defaultT("update")} onClick={publishSamlConfig} />
        </div>
      </PageBox>
    </div>
  );
};

export default EditSamlConfigPage;
